import { createBrowserHistory } from 'history';

const browserHistory = createBrowserHistory();

/**
 * Prepend a router base path to an absolute navigation target.
 *
 * `history@5` ignores createBrowserHistory's `basename`, and the app navigates
 * imperatively via `browserHistory.push(...)` / `forceReplace` throughout. When
 * the app is served under a sub-path (e.g. a PR preview at `/pr-123/`), those
 * imperative navigations must include the base or React Router can't match the
 * route and renders nothing.
 *
 * Leaves untouched: relative paths, full URLs, and targets already under `base`
 * (including the bare base itself). No-op when `base` is empty (BASE_URL "/").
 *
 * @param {string} base - base path without a trailing slash (e.g. `/pr-123`), or `''` to disable.
 * @param {string | { pathname?: string }} to - a history target (path string or location object).
 * @returns {string | { pathname?: string }} the target, prefixed when appropriate.
 */
export function applyBasename(base, to) {
  if (!base) return to;

  if (typeof to === 'string') {
    return to.startsWith('/') && !to.startsWith(`${base}/`) && to !== base ? `${base}${to}` : to;
  }

  if (
    to &&
    typeof to.pathname === 'string' &&
    to.pathname.startsWith('/') &&
    !to.pathname.startsWith(`${base}/`) &&
    to.pathname !== base
  ) {
    return { ...to, pathname: `${base}${to.pathname}` };
  }

  return to;
}

const routerBase = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
if (routerBase) {
  const originalPush = browserHistory.push.bind(browserHistory);
  const originalReplace = browserHistory.replace.bind(browserHistory);
  browserHistory.push = (to, state) => originalPush(applyBasename(routerBase, to), state);
  browserHistory.replace = (to, state) => originalReplace(applyBasename(routerBase, to), state);
}

let activeUnblockFunctions = new Set();

export function registerNavigationBlocker(unblockFn) {
  activeUnblockFunctions.add(unblockFn);
  return () => activeUnblockFunctions.delete(unblockFn);
}

export function temporarilyDisableAllNavigationBlockers() {
  // Temporarily remove all navigation blockers
  const currentBlockers = Array.from(activeUnblockFunctions);
  currentBlockers.forEach((unblock) => {
    if (typeof unblock === 'function') {
      unblock();
    }
  });
  activeUnblockFunctions.clear();
  return currentBlockers;
}

export function forceReplace(path, state) {
  // Disable all navigation blockers temporarily
  temporarilyDisableAllNavigationBlockers();

  // Perform the navigation
  browserHistory.replace(path, state);
}

export default browserHistory;
